import React, { useState } from 'react';
import { Bell, MessageSquare, Users, Bot, X, Check, CheckSquare, Send, Edit3, Trash2 } from 'lucide-react';

// Notificaciones vacías - modo real
const mockNotifications = [];

const iconMap = {
  message: MessageSquare,
  user: Users,
  ai: Bot,
  task: CheckSquare
};

const colorMap = {
  message: 'bg-blue-100 text-blue-600',
  user: 'bg-green-100 text-green-600',
  ai: 'bg-purple-100 text-purple-600',
  task: 'bg-yellow-100 text-yellow-600'
};

export default function NotificationsDropdown({ onClose, onNavigate, onOpenXIA, notifications = mockNotifications, onNotificationsChange }) {
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [showAllModal, setShowAllModal] = useState(false);

  // Función auxiliar para actualizar notificaciones
  const updateNotifications = (updater) => {
    if (onNotificationsChange) {
      onNotificationsChange(updater);
    }
  };

  // Marcar todas como leídas y borrar automáticamente
  const handleMarkAllRead = () => {
    updateNotifications(prev => []); // Borrar todas al marcar como leídas
  };

  // Marcar una notificación como leída (la borra automáticamente)
  const markAsRead = (id) => {
    updateNotifications(prev => prev.filter(n => n.id !== id)); // Borrar al marcar como leída
  };

  // Borrar notificación
  const deleteNotification = (id, e) => {
    e.stopPropagation();
    updateNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Editar notificación (abrir popup para editar)
  const editNotification = (notification, e) => {
    e.stopPropagation();
    setSelectedNotification(notification);
    setReplyText(notification.message);
  };

  // Contar no leídas
  const unreadCount = notifications.filter(n => !n.read).length;

  const handleNotificationClick = (notification) => {
    // Marcar como leída y abrir popup
    markAsRead(notification.id);
    setSelectedNotification(notification);
  };

  const handleReply = () => {
    if (!replyText.trim()) return;

    // Si es XIA o tarea, abrir el chat de XIA con el mensaje
    if (onOpenXIA && (selectedNotification.type === 'ai' || selectedNotification.type === 'task')) {
      onOpenXIA(replyText);
    }

    setReplyText('');
    setSelectedNotification(null);
    onClose();
  };

  const handleClosePopup = () => {
    setSelectedNotification(null);
    setReplyText('');
  };

  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border z-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <h3 className="font-semibold">
          Notificaciones
          {unreadCount > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
              {unreadCount}
            </span>
          )}
        </h3>
        <div className="flex items-center space-x-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-xs text-blue-600 hover:underline"
            >
              Marcar todo leido
            </button>
          )}
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="max-h-80 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No tienes notificaciones</p>
          </div>
        ) : (
          notifications.map(notification => {
            const Icon = iconMap[notification.type] || Bell;
            const colorClass = colorMap[notification.type] || 'bg-gray-100 text-gray-600';

            return (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`p-4 border-b hover:bg-gray-50 cursor-pointer transition group ${
                  !notification.read ? 'bg-blue-50/50' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-full ${colorClass}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm truncate">{notification.title}</p>
                      <div className="flex items-center gap-1">
                        {!notification.read && (
                          <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                        )}
                        {/* Botones editar/borrar */}
                        <button
                          onClick={(e) => editNotification(notification, e)}
                          className="p-1 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100 transition"
                          title="Editar"
                        >
                          <Edit3 className="w-3.5 h-3.5 text-gray-500" />
                        </button>
                        <button
                          onClick={(e) => deleteNotification(notification.id, e)}
                          className="p-1 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition"
                          title="Borrar"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{notification.description}</p>
                    <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="p-3 border-t bg-gray-50 text-center">
          <button
            onClick={() => setShowAllModal(true)}
            className="text-sm text-blue-600 hover:underline"
          >
            Ver todas las notificaciones
          </button>
        </div>
      )}

      {/* Popup de mensaje */}
      {selectedNotification && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" onClick={handleClosePopup}>
          <div className="bg-white rounded-xl shadow-2xl w-96 max-w-[90vw] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header del popup */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = iconMap[selectedNotification.type] || Bell;
                  const colorClass = colorMap[selectedNotification.type] || 'bg-gray-100 text-gray-600';
                  return (
                    <div className={`p-2 rounded-full ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                  );
                })()}
                <div>
                  <p className="font-semibold">{selectedNotification.from}</p>
                  <p className="text-xs text-gray-500">{selectedNotification.time}</p>
                </div>
              </div>
              <button onClick={handleClosePopup} className="p-1 hover:bg-gray-200 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Contenido del mensaje */}
            <div className="p-4">
              <p className="text-gray-800">{selectedNotification.message}</p>
            </div>

            {/* Área de respuesta */}
            <div className="p-4 border-t bg-gray-50">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleReply()}
                  placeholder="Escribe una respuesta..."
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3B82F6]"
                  autoFocus
                />
                <button
                  onClick={handleReply}
                  disabled={!replyText.trim()}
                  className="p-2 bg-[#3B82F6] text-white rounded-lg hover:bg-[#2563EB] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {selectedNotification.type === 'ai' || selectedNotification.type === 'task'
                  ? 'Tu respuesta se enviará a XIA'
                  : 'Tu respuesta se enviará al chat'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ver Todas las Notificaciones */}
      {showAllModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" onClick={() => setShowAllModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-[600px] max-w-[90vw] max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header del modal */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <h2 className="font-semibold text-lg">Todas las Notificaciones</h2>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Marcar todo leído
                  </button>
                )}
                <button onClick={() => setShowAllModal(false)} className="p-1 hover:bg-gray-200 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Lista de notificaciones */}
            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <Bell className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No tienes notificaciones</p>
                </div>
              ) : (
                notifications.map(notification => {
                  const Icon = iconMap[notification.type] || Bell;
                  const colorClass = colorMap[notification.type] || 'bg-gray-100 text-gray-600';

                  return (
                    <div
                      key={notification.id}
                      onClick={() => { handleNotificationClick(notification); setShowAllModal(false); }}
                      className={`p-4 border-b hover:bg-gray-50 cursor-pointer transition group ${
                        !notification.read ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`p-3 rounded-full ${colorClass}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium">{notification.title}</p>
                            <div className="flex items-center gap-2">
                              {!notification.read && (
                                <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                              )}
                              <button
                                onClick={(e) => deleteNotification(notification.id, e)}
                                className="p-1 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition"
                                title="Borrar"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </button>
                            </div>
                          </div>
                          <p className="text-gray-600 mt-1">{notification.message}</p>
                          <p className="text-sm text-gray-400 mt-2">{notification.time}</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer del modal */}
            <div className="p-4 border-t bg-gray-50 text-center text-sm text-gray-500">
              {notifications.length} notificaciones en total
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
